import React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useAuth } from 'shared/contexts/AuthContext';
import { UserRole } from 'shared/types';
import { getDefaultRouteForRole } from 'shared/utils/roleRedirects';

const ALWAYS_BLOCKED: readonly UserRole[] = [
  UserRole.CLIENT_APPROVER,
  UserRole.CLIENT_STAFF,
  UserRole.SECURITY,
];

/**
 * Guards /companies/:companyId/*. Platform roles (super_admin, safety_staff)
 * pass straight through. company_admin is let through too, but only when
 * :companyId is their own company — the whole subtree (resumen, users,
 * verifications, service-cards, etc.) is already fully defined here, so this
 * just lets company_admin view their own company at the same URL super_admin
 * uses, instead of always bouncing them to /my-company. See the matching
 * `isOwnCompanyUrl` checks in CompanyDetail.tsx that keep company_admin-only
 * affordances (edit, chat with soporte monitor) working the same way whether
 * they arrive via /my-company or via /companies/:ownCompanyId. company_admin
 * viewing a company that isn't their own stays blocked, as before.
 */
export const CompanyContextGuard: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { user, hasRole } = useAuth();
  const { companyId } = useParams();

  if (!user?.role) {
    return <Navigate to="/login" replace />;
  }

  if (ALWAYS_BLOCKED.some(role => hasRole(role))) {
    return <Navigate to={getDefaultRouteForRole(user.role)} replace />;
  }

  if (hasRole(UserRole.COMPANY_ADMIN) && companyId !== user.company?._id) {
    return <Navigate to={getDefaultRouteForRole(user.role)} replace />;
  }

  return children;
};

export default CompanyContextGuard;
